import { Button, SearchField, SearchIcon } from '@iheartjane/reefer';
import { clsx } from 'clsx';
import { useEffect, useRef } from 'react';
import { useMatch } from 'react-router';

import { StoreSwitcher } from '@jane/bloom/components/storeSwitcher';
import { StoreSwitcherToggle } from '@jane/bloom/components/storeSwitcher/storeSwitcherToggle';
import { useOptimisticBagSize } from '@jane/bloom/hooks/useBagSize';
import { useDetectKeyboard } from '@jane/bloom/hooks/useDetectKeyboard';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useScrollLock } from '@jane/bloom/hooks/useScrollLock';
import { useUserSearchQuery } from '@jane/bloom/hooks/useUserSearchQuery';
import { trackHeaderTap } from '@jane/bloom/services/tracking/track';
import { useIsDraft } from '@jane/bloom/utils/queryParams/isDraft';
import { SEARCH_TERM_QUERY_KEY } from '@jane/bloom/utils/queryParams/queryParams';
import type { JaneGold } from '@jane/bloom/viewModels/productDetail';

import { FormWithBasename } from './formWithBasename';
import styles from './header.module.css';
import { AccountInfo } from './header/accountInfo';
import { CartInfo } from './header/cartInfo';
import { MainMenu } from './header/mainMenu';
import { SearchSuggestions } from './header/searchSuggestions';
import { LinkWithBasename } from './linkWithBasename';

type HeaderProps = {
  borderless?: boolean;
  disableAccountRedirect?: boolean;
  dismissLoginPrompt?: () => void;
  janeGold?: JaneGold;
  showLoginPrompt?: boolean;
};

export function Header({
  borderless = false,
  disableAccountRedirect = false,
  dismissLoginPrompt,
  janeGold,
  showLoginPrompt = false,
}: HeaderProps) {
  const headerRef = useRef<HTMLElement>(null);
  const isDraft = useIsDraft();
  const {
    featureFlags,
    linkToMainSite,
    logo,
    menuUrl,
    pinnedStore,
    shoppingPreferences,
    theme,
  } = useRootLoaderData();
  const bagSize = useOptimisticBagSize();
  const {
    hideSearchSuggestions,
    searchModalQuery,
    setSearchModalQuery,
    shouldOpenSearchModal,
    showSearchSuggestions,
    transition,
  } = useUserSearchQuery();
  const { setScrollLock } = useScrollLock();
  const isCheckout = useMatch(':basename/checkout/*');
  const hasMainMenuItems =
    theme.bloom?.main_menu && theme.bloom?.main_menu.length > 0;
  const mainSiteUrl = menuUrl ? new URL(menuUrl).origin : '';

  useEffect(() => {
    setScrollLock(shouldOpenSearchModal);
  }, [setScrollLock, shouldOpenSearchModal]);

  const isKeyboardOpen = useDetectKeyboard({
    disableDetection: !isCheckout,
    interval: 100,
  });

  useEffect(() => {
    /**
     * Mobile Safari adds whitespace to the bottom of the page when the virtual keyboard is open
     * It results in the header scrolling off the page and being inaccesible when the keyboard closes
     * If we are in checkout, scroll the header into view when the keyboard closes
     */
    if (!isKeyboardOpen && isCheckout) {
      headerRef.current?.scrollIntoView?.({ behavior: 'smooth' });
    }
  }, [isKeyboardOpen, isCheckout]);

  const commonLinkProps = {
    'aria-label': `${pinnedStore?.name} home`,
    className: clsx('focus-visible', styles['storeLogoLink']),
    onClick: () =>
      trackHeaderTap(
        {
          reservationMode: shoppingPreferences?.reservationMode,
          tappable: 'logo',
        },
        featureFlags
      ),
  };
  const logoImage = logo?.small && (
    <img
      height={48}
      loading="eager"
      src={logo.small}
      alt={`${pinnedStore?.name ?? 'store'} logo`}
      className={styles['storeLogo']}
    />
  );

  return (
    <>
      <header
        className={clsx(styles['header'], {
          [styles['header--borderless']]: borderless,
        })}
        ref={headerRef}
      >
        <div className={styles['headerContent']}>
          {linkToMainSite ? (
            <a {...commonLinkProps} href={mainSiteUrl}>
              {logoImage}
            </a>
          ) : (
            <LinkWithBasename {...commonLinkProps} to="/">
              {logoImage}
            </LinkWithBasename>
          )}

          <div
            className={clsx({
              [styles['storeSwitcher--desktop']]: !isCheckout,
            })}
          >
            {/*
              `id="store-name"` is attached only to the desktop instance.
              The toggle is rendered twice (desktop + mobile, swapped via CSS)
              so attaching the id to both would produce a duplicate id in the DOM.
              The desktop instance is rendered first, so getElementById finds it
              regardless of viewport.
            */}
            <StoreSwitcherToggle
              hidden={!!isCheckout}
              storeNameId="store-name"
            />
          </div>

          <div className={styles['searchForm']}>
            <FormWithBasename
              action="/search"
              method="get"
              name="Search Form"
              onSubmit={() => {
                hideSearchSuggestions();
              }}
            >
              <SearchField
                className={styles['searchField']}
                label="Search"
                placeholder={`Search ${pinnedStore?.name}`}
                name={SEARCH_TERM_QUERY_KEY}
                onChange={(val) => setSearchModalQuery(val)}
                isDebounced={false}
                borderRadius="lg"
                value={searchModalQuery}
                disableMobileInputStyling
                onFocus={() => {
                  showSearchSuggestions();
                  trackHeaderTap(
                    {
                      reservationMode: shoppingPreferences?.reservationMode,
                      tappable: 'search icon',
                    },
                    featureFlags
                  );
                }}
                onBlur={() => hideSearchSuggestions()}
              />
            </FormWithBasename>
            <SearchSuggestions
              hideSearchSuggestions={hideSearchSuggestions}
              placeholder={`Search ${pinnedStore?.name}`}
              searchModalQuery={searchModalQuery}
              setSearchModalQuery={setSearchModalQuery}
              transition={transition}
              janeGold={janeGold}
            />
          </div>

          <div className={styles['icons']}>
            <Button.Icon
              ariaLabel="Search"
              className={styles['searchIcon']}
              icon={
                <SearchIcon className={styles['searchIcon']} color="inherit" />
              }
              onClick={() => {
                showSearchSuggestions();
                trackHeaderTap(
                  {
                    reservationMode: shoppingPreferences?.reservationMode,
                    tappable: 'search icon',
                  },
                  featureFlags
                );
              }}
            />
            <AccountInfo
              disabled={isDraft}
              disableRedirect={disableAccountRedirect}
              {...(showLoginPrompt && dismissLoginPrompt
                ? {
                    dismissPrompt: dismissLoginPrompt,
                    showPrompt: true,
                  }
                : {})}
            />
            <CartInfo count={bagSize} disabled={isDraft} />
            {hasMainMenuItems && <MainMenu />}
          </div>
          <StoreSwitcher />
        </div>
      </header>
      <div
        className={clsx({
          [styles['storeSwitcher--mobile']]: !isCheckout,
        })}
      >
        <StoreSwitcherToggle hidden={!!isCheckout} />
      </div>
    </>
  );
}
